<template>
  <v-container fluid>
    <v-row no-gutters>
      <v-col cols="12" lg="6" sm="12">
        <v-card>
          <v-list-item>
            <v-list-item-avatar>
                <v-icon>mdi-message-bulleted</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
                <v-list-item-title class="headline">システムお知らせ情報編集</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-card-text>
            <v-form ref="system_notification_form" v-model="valid" @submit.prevent>
              <v-menu min-width="290px" max-width="290px">
                <template v-slot:activator="{ on }">
                  <v-text-field v-on="on" label="表示予定日" v-model="scheduledDisplayFormatted" dense outlined readonly @blur="scheduledDisplayDate = parseDate(scheduledDisplayFormatted)"/>
                </template>
                <v-date-picker v-model="scheduledDisplayDate" no-title scrollable actions locale="ja-jp" :close-on-content-click="true" :day-format="date => new Date(date).getDate()" :min="today"/>
              </v-menu>
              <v-menu min-width="290px" max-width="290px">
                <template v-slot:activator="{ on }">
                  <v-text-field v-on="on" label="表示期限日" v-model="displayPeriodFormatted" dense outlined readonly @blur="displayPeriod = parseDate(displayPeriodFormatted)"/>
                </template>
                <v-date-picker v-model="displayPeriod" no-title scrollable actions locale="ja-jp" :close-on-content-click="true" :day-format="date => new Date(date).getDate()" :min="today"/>
              </v-menu>
              <v-text-field label="タイトル" v-model="title" :rules="notificationTitleRules" dense outlined>
                <template v-slot:append>
                  <v-chip small color="red" text-color="white"><strong>必須</strong></v-chip>
                </template>
              </v-text-field>
              <v-textarea label="お知らせ内容" v-model="content" rows="5" :rules="notificationContentRules" counter="1500" outlined>
                <template v-slot:append>
                  <v-chip small color="red" text-color="white"><strong>必須</strong></v-chip>
                </template>
              </v-textarea>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey lighten-1" class="mr-3" :to="{ name: 'SystemNotification' }" depressed>キャンセル</v-btn>
            <v-btn color="primary" @click.prevent="checkDialog" :disabled="!valid" depressed>確認</v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
    <v-row justify="center">
      <custom-dialog :maxWith="dialogMaxWith" :title="dialogTitle" :items="dialogItems" :drawer="dialogDrawer" visibleUpdate
        @updated="submit" @canceled="dialogCancel"/>
    </v-row>
  </v-container>
</template>

<script>
import { useAppInsights } from '@/plugins/application-insights';
import CustomDialog from "@/components/custom_ui/CustomDialog";
import SystemNotificationsApi from "@/api/system-notifications-api";
import Rule from "@/validation/rules";
import statusList from "@/helpers/const";

export default {
  name: "SystemNotificationEdit",
  components: {
    CustomDialog
  },
  data: () => ({
    loading: false,
    // 表示予定日
    scheduledDisplayFormatted: "",
    scheduledDisplayDate: "",
    // 表示期限日
    displayPeriodFormatted: "",
    displayPeriod: "",
    // タイトル
    title: "",
    // お知らせ内容
    content: "",
    // バリデーション
    valid: true,
    today: new Date().toISOString().substr(0,10),
    notificationTitleRules: Rule.notificationTitleRules,
    notificationContentRules: Rule.notificationContentRules,
    // 更新ダイアログ
    dialogMaxWith: 500,
    dialogTitle: "",
    dialogItems: [],
    dialogDrawer: false,
    editId: null,
  }),
  created() {
    this.initialize();
  },
  watch: {
    'scheduledDisplayDate': function(){
      if (this.displayPeriod < this.scheduledDisplayDate) {
          this.displayPeriod = this.scheduledDisplayDate;
      }
      this.scheduledDisplayFormatted = this.formatDate(this.scheduledDisplayDate);
    },
    'displayPeriod': function(){
      if (this.scheduledDisplayDate > this.displayPeriod) {
          this.scheduledDisplayDate = this.displayPeriod;
      }
      this.displayPeriodFormatted = this.formatDate(this.displayPeriod);
    },
  },
  methods: {
    isLoading() {
      this.loading = !this.loading;
      this.$emit("loading", this.loading);
    },
    initialize() {
      this.isLoading();
      this.systemNotificationsApi = new SystemNotificationsApi();
      // システムお知らせ情報IDをルートパラメータから取得
      this.editId = this.$route.params.id;
      const promise = this.systemNotificationsApi.info(this.editId);
      promise.then(response => {
        this.scheduledDisplayDate = this.parseDate(response.data.body.system_notifications_info.scheduled_display_date);
        this.displayPeriod = this.parseDate(response.data.body.system_notifications_info.display_period);
        this.title = response.data.body.system_notifications_info.title;
        this.content = response.data.body.system_notifications_info.content;
      }).catch(error => {
        useAppInsights().trackException({exception: error});
        if (error.response) {
          this.$emit("error",error.response.data.message);
          if (error.response.data.status == statusList.status.authError) {
            this.dialogDrawer = false;
            this.$emit('logoutConform');
          } else if (error.response.data.status == statusList.status.entityError) {
            this.$router.push({ name: "SystemNotificationEdit" })
          }
        } else {
          this.$emit("error","サーバーエラーが発生しました。");
        }
      }).finally(() => {
        this.isLoading();
      });
    },
    // 日付フォーマット
    formatDate (date) {
      if (!date) return null;
      const [year, month, day] = date.split('-');
      return `${year}/${month}/${day}`;
    },
    parseDate (date) {
      if (!date) return null;
      const [year,month, day] = date.split('/');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    },
    //更新ボタン押下時
    checkDialog() {
      this.dialogTitle = "更新しますか？";
      this.dialogItems = [
        { name: "表示予定日", value: this.scheduledDisplayFormatted },
        { name: "表示期限日", value: this.displayPeriodFormatted },
        { name: "タイトル", value: this.title },
        { name: "お知らせ内容", value: this.content }
      ];
      this.dialogDrawer = true;
    },
    dialogCancel() {
      this.dialogDrawer = false;
    },
    //ダイアログ更新ボタン押下時
    submit() {
      if (this.$refs.system_notification_form.validate()) {
        this.isLoading();
        const params = {
          scheduled_display_date: this.scheduledDisplayDate,
          display_period: this.displayPeriod,
          title: this.title,
          content: this.content
        };
        const promise = this.systemNotificationsApi.update(this.editId, params);
        promise.then(response => {
          this.$router.push({ name: 'SystemNotification' });
          this.$emit("success", response.data.message);
        }).catch(error => {
          useAppInsights().trackException({exception: error});
          if (error.response) {
            if (error.response.data.status == statusList.status.authError) {
              this.dialogDrawer = false;
              this.$emit('logoutConform');
            } else {
              this.$emit("error",error.response.data.message);
            }
          } else {
            this.$emit("error","サーバーエラーが発生しました。");
          }
        }).finally(() => {
          this.isLoading();
        });
      }
    }
  },
};
</script>
